
import { useAuth } from '@/contexts/AuthContext';
import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { PEODashboardHeader } from './PEODashboardHeader';
import { PEOProviderProfile } from './PEOProviderProfile';
import { PEODashboardContent } from "@/components/peo/dashboard/PEODashboardContent";
import { usePEOProviderData } from "@/components/peo/dashboard/usePEOProviderData";
import { PEODashboardLoader } from "./PEODashboardLoader";
import { useNavigate } from "react-router-dom";

/**
 * The main PEO Dashboard page with preventive loader.
 * Prevents a flash of the restricted page by keeping the loader active
 * until both authentication and provider data are fully loaded.
 */
const PEODashboard = () => {
  const { logout, userRole, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const {
    loading,
    providerInfo,
    isEditing,
    setIsEditing,
    form,
    setForm,
    handleSave,
    saving,
  } = usePEOProviderData();

  // Combined loading state - show loader if either auth is loading or provider data is loading
  const isLoading = authLoading || loading || !initialLoadComplete;

  // When provider data loads, mark initial load as complete
  useEffect(() => {
    if (!loading && !authLoading && providerInfo) {
      // Small delay to ensure everything is ready before showing content
      const timer = setTimeout(() => {
        setInitialLoadComplete(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, authLoading, providerInfo]);

  // Handles dashboard manual refresh
  const handleRefresh = () => {
    window.location.reload();
  };

  // Loader fully covers page while data loads
  if (isLoading) {
    return <PEODashboardLoader />;
  }

  return (
    <PEODashboardContent
      loading={loading}
      onRefresh={handleRefresh}
      onLogout={logout}
      providerInfo={providerInfo}
      isEditing={isEditing}
      setIsEditing={setIsEditing}
      form={form}
      setForm={setForm}
      onSave={handleSave}
      saving={saving}
    />
  );
};

export default PEODashboard;
